import { placeholderTextMessages as messages } from '@atlaskit/editor-common/messages';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { bracketTyped, browser, isEmptyDocument, isEmptyParagraph } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { findParentNode } from '@atlaskit/editor-prosemirror/utils';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
// Typewriter animation timing constants
var TYPEWRITER_TYPE_DELAY = 50; // Delay between typing each character
var TYPEWRITER_PAUSE_BEFORE_ERASE = 2000; // Pause before starting to erase text
var TYPEWRITER_ERASE_DELAY = 40; // Delay between erasing each character
var TYPEWRITER_CYCLE_DELAY = 500; // Delay before starting next cycle
var TYPEWRITER_TYPED_AND_DELETED_DELAY = 1500; // Delay before starting animation after user typed and deleted

export var pluginKey = new PluginKey('placeholderPlugin');
var placeholderTestId = 'placeholder-test-id';
function getPlaceholderState(editorState) {
  return pluginKey.getState(editorState);
}
var nodeTypesWithLongPlaceholderText = ['expand', 'panel'];
var nodeTypesWithShortPlaceholderText = ['tableCell', 'tableHeader'];
var cycleThroughPlaceholderPrompts = function cycleThroughPlaceholderPrompts(placeholderPrompts, activeTypewriterTimeouts, placeholderNodeWithText) {
  var initialDelayWhenUserTypedAndDeleted = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var currentPromptIndex = 0;
  var displayedText = '';
  var animationTimeouts = [];
  var clearAllTimeouts = function clearAllTimeouts() {
    animationTimeouts.forEach(function (timeoutId) {
      return clearTimeout(timeoutId);
    });
    animationTimeouts = [];
  };
  var scheduleTimeout = function scheduleTimeout(callback, delay) {
    var timeoutId = setTimeout(callback, delay);
    animationTimeouts.push(timeoutId);
    return timeoutId;
  };
  var _startAnimationCycle = function startAnimationCycle() {
    var currentPrompt = placeholderPrompts[currentPromptIndex];
    var characterIndex = 0;
    var _typeNextCharacter = function typeNextCharacter() {
      if (characterIndex < currentPrompt.length) {
        displayedText = currentPrompt.substring(0, characterIndex + 1);
        placeholderNodeWithText.textContent = displayedText;
        characterIndex++;
        scheduleTimeout(_typeNextCharacter, TYPEWRITER_TYPE_DELAY);
      } else {
        scheduleTimeout(_eraseLastCharacter, TYPEWRITER_PAUSE_BEFORE_ERASE);
      }
    };
    var _eraseLastCharacter = function eraseLastCharacter() {
      if (displayedText.length > 1) {
        displayedText = displayedText.substring(0, displayedText.length - 1);
        placeholderNodeWithText.textContent = displayedText;
        scheduleTimeout(_eraseLastCharacter, TYPEWRITER_ERASE_DELAY);
      } else {
        displayedText = ' ';
        placeholderNodeWithText.textContent = displayedText;
        currentPromptIndex = (currentPromptIndex + 1) % placeholderPrompts.length;
        scheduleTimeout(_startAnimationCycle, TYPEWRITER_CYCLE_DELAY);
      }
    };
    _typeNextCharacter();
  };
  activeTypewriterTimeouts === null || activeTypewriterTimeouts === void 0 || activeTypewriterTimeouts.push(clearAllTimeouts);
  if (initialDelayWhenUserTypedAndDeleted > 0) {
    placeholderNodeWithText.textContent = ' ';
    scheduleTimeout(_startAnimationCycle, initialDelayWhenUserTypedAndDeleted);
  } else {
    _startAnimationCycle();
  }
};
export function createPlaceholderDecoration(editorState, placeholderText, placeholderPrompts, activeTypewriterTimeouts) {
  var pos = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 1;
  var initialDelayWhenUserTypedAndDeleted = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;
  var placeholderDecoration = document.createElement('span');
  var placeholderNodeWithText = placeholderDecoration;
  placeholderDecoration.setAttribute('data-testid', placeholderTestId);
  placeholderDecoration.className = 'placeholder-decoration';

  // PM sets contenteditable to false on Decorations so Firefox doesn't display the flashing cursor
  // So adding an extra span which will contain the placeholder text
  if (browser.gecko) {
    var placeholderNode = document.createElement('span');
    placeholderNode.setAttribute('contenteditable', 'true'); // explicitly overriding the default Decoration behaviour
    placeholderDecoration.appendChild(placeholderNode);
    placeholderNodeWithText = placeholderNode;
  }
  if (placeholderText) {
    placeholderNodeWithText.textContent = placeholderText || ' ';
  } else if (placeholderPrompts) {
    cycleThroughPlaceholderPrompts(placeholderPrompts, activeTypewriterTimeouts, placeholderNodeWithText, initialDelayWhenUserTypedAndDeleted);
  }

  // ME-2289 Tapping on backspace in empty editor hides and displays the keyboard
  // Add a editable buff node as the cursor moving forward is inevitable
  // when backspace in GBoard composition
  if (browser.android && browser.chrome) {
    var buffNode = document.createElement('span');
    buffNode.setAttribute('class', 'placeholder-android');
    buffNode.setAttribute('contenteditable', 'true');
    buffNode.textContent = ' ';
    placeholderDecoration.appendChild(buffNode);
  }
  var isTargetNested = editorState.doc.resolve(pos).depth > 1;

  // only truncate text for nested nodes, otherwise applying 'overflow: hidden;' to top level nodes
  // creates issues with quick insert button
  if (isTargetNested && editorExperiment('platform_editor_controls', 'variant1')) {
    placeholderDecoration.classList.add('placeholder-decoration-hide-overflow');
  }
  return DecorationSet.create(editorState.doc, [Decoration.widget(pos, placeholderDecoration, {
    side: 0,
    key: "placeholder ".concat(placeholderText)
  })]);
}
function setPlaceHolderState(placeholderText, pos, placeholderPrompts, typedAndDeleted, userHadTyped) {
  return {
    hasPlaceholder: true,
    placeholderText: placeholderText,
    placeholderPrompts: placeholderPrompts,
    pos: pos ? pos : 1,
    typedAndDeleted: typedAndDeleted,
    userHadTyped: userHadTyped
  };
}
var emptyPlaceholder = function emptyPlaceholder(placeholderText, placeholderPrompts, userHadTyped) {
  return {
    hasPlaceholder: false,
    placeholderText: placeholderText,
    placeholderPrompts: placeholderPrompts,
    userHadTyped: userHadTyped,
    typedAndDeleted: false
  };
};
function createPlaceHolderStateFrom(_ref) {
  var isEditorFocused = _ref.isEditorFocused,
    editorState = _ref.editorState,
    isTypeAheadOpen = _ref.isTypeAheadOpen,
    defaultPlaceholderText = _ref.defaultPlaceholderText,
    intl = _ref.intl,
    bracketPlaceholderText = _ref.bracketPlaceholderText,
    emptyLinePlaceholder = _ref.emptyLinePlaceholder,
    placeholderPrompts = _ref.placeholderPrompts,
    typedAndDeleted = _ref.typedAndDeleted,
    userHadTyped = _ref.userHadTyped;
  if (isTypeAheadOpen !== null && isTypeAheadOpen !== void 0 && isTypeAheadOpen(editorState)) {
    return emptyPlaceholder(defaultPlaceholderText, placeholderPrompts, userHadTyped);
  }
  if ((defaultPlaceholderText || placeholderPrompts) && isEmptyDocument(editorState.doc)) {
    return setPlaceHolderState(defaultPlaceholderText, 1, placeholderPrompts, typedAndDeleted, userHadTyped);
  }
  if (isEditorFocused && editorExperiment('platform_editor_controls', 'variant1')) {
    var _parentNode$firstChil, _parentNode$firstChil2;
    var _editorState$selectio = editorState.selection,
      $from = _editorState$selectio.$from,
      $to = _editorState$selectio.$to;
    if ($from.pos !== $to.pos) {
      return emptyPlaceholder(defaultPlaceholderText, placeholderPrompts, userHadTyped);
    }
    var parentNode = $from.node($from.depth - 1);
    var parentType = parentNode === null || parentNode === void 0 ? void 0 : parentNode.type.name;
    if (emptyLinePlaceholder && parentType === 'doc') {
      var isEmptyLine = isEmptyParagraph($from.parent);
      if (isEmptyLine) {
        return setPlaceHolderState(emptyLinePlaceholder, $from.pos, placeholderPrompts, typedAndDeleted, userHadTyped);
      }
    }
    var isEmptyNode = (parentNode === null || parentNode === void 0 ? void 0 : parentNode.childCount) === 1 && ((_parentNode$firstChil = parentNode.firstChild) === null || _parentNode$firstChil === void 0 ? void 0 : _parentNode$firstChil.content.size) === 0 && ((_parentNode$firstChil2 = parentNode.firstChild) === null || _parentNode$firstChil2 === void 0 ? void 0 : _parentNode$firstChil2.type.name) === 'paragraph';
    if (nodeTypesWithShortPlaceholderText.includes(parentType) && isEmptyNode) {
      var _table$node$firstChil;
      var table = findParentNode(function (node) {
        return node.type === editorState.schema.nodes.table;
      })(editorState.selection);
      if (!table) {
        return emptyPlaceholder(defaultPlaceholderText, placeholderPrompts, userHadTyped);
      }
      var isFirstCell = (table === null || table === void 0 || (_table$node$firstChil = table.node.firstChild) === null || _table$node$firstChil === void 0 ? void 0 : _table$node$firstChil.content.firstChild) === parentNode;
      if (isFirstCell) {
        return setPlaceHolderState(intl.formatMessage(messages.shortEmptyNodePlaceholderText), $from.pos, placeholderPrompts, typedAndDeleted, userHadTyped);
      }
    }
    if (nodeTypesWithLongPlaceholderText.includes(parentType) && isEmptyNode) {
      return setPlaceHolderState(intl.formatMessage(messages.longEmptyNodePlaceholderText), $from.pos, placeholderPrompts, typedAndDeleted, userHadTyped);
    }
    return emptyPlaceholder(defaultPlaceholderText, placeholderPrompts, userHadTyped);
  }
  if (bracketPlaceholderText && bracketTyped(editorState) && isEditorFocused) {
    var _$from = editorState.selection.$from;
    // Space is to account for positioning of the bracket
    var bracketHint = '  ' + bracketPlaceholderText;
    return setPlaceHolderState(bracketHint, _$from.pos - 1, placeholderPrompts, typedAndDeleted, userHadTyped);
  }
  return emptyPlaceholder(defaultPlaceholderText, placeholderPrompts, userHadTyped);
}
function calculateUserInteractionState(_ref2) {
  var placeholderState = _ref2.placeholderState,
    oldEditorState = _ref2.oldEditorState,
    newEditorState = _ref2.newEditorState;
  var wasEmpty = oldEditorState ? isEmptyDocument(oldEditorState.doc) : true;
  var isEmpty = isEmptyDocument(newEditorState.doc);
  var hasEverTyped = Boolean(placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.userHadTyped) ||
  // Previously typed
  !wasEmpty ||
  // Had content before
  wasEmpty && !isEmpty; // Just added content
  var justDeletedAll = hasEverTyped && isEmpty && !wasEmpty;
  var isInTypedAndDeletedState = justDeletedAll || Boolean(placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.typedAndDeleted) && isEmpty;
  // Only reset user interaction tracking when editor is cleanly empty
  var shouldResetInteraction = isEmpty && !isInTypedAndDeletedState;
  return {
    userHadTyped: shouldResetInteraction ? false : hasEverTyped,
    typedAndDeleted: isInTypedAndDeletedState
  };
}
export function createPlugin(intl, defaultPlaceholderText, bracketPlaceholderText, emptyLinePlaceholder, placeholderPrompts, api) {
  if (!defaultPlaceholderText && !placeholderPrompts && !bracketPlaceholderText) {
    return;
  }
  var isDestroyed = false;
  var activeTypewriterTimeouts = [];
  var clearAllTypewriterTimeouts = function clearAllTypewriterTimeouts() {
    activeTypewriterTimeouts.forEach(function (clearFn) {
      return clearFn();
    });
    activeTypewriterTimeouts = [];
  };
  return new SafePlugin({
    key: pluginKey,
    state: {
      init: function init(_, state) {
        var _api$focus, _api$typeAhead;
        return createPlaceHolderStateFrom({
          isEditorFocused: Boolean(api === null || api === void 0 || (_api$focus = api.focus) === null || _api$focus === void 0 || (_api$focus = _api$focus.sharedState.currentState()) === null || _api$focus === void 0 ? void 0 : _api$focus.hasFocus),
          editorState: state,
          isTypeAheadOpen: api === null || api === void 0 || (_api$typeAhead = api.typeAhead) === null || _api$typeAhead === void 0 ? void 0 : _api$typeAhead.actions.isOpen,
          defaultPlaceholderText: defaultPlaceholderText,
          bracketPlaceholderText: bracketPlaceholderText,
          emptyLinePlaceholder: emptyLinePlaceholder,
          placeholderPrompts: placeholderPrompts,
          typedAndDeleted: false,
          userHadTyped: false,
          intl: intl
        });
      },
      apply: function apply(tr, placeholderState, _oldEditorState, newEditorState) {
        var _api$focus2, _api$typeAhead2, _ref3, _meta$placeholderText, _ref4, _meta$placeholderProm;
        var meta = tr.getMeta(pluginKey);
        var isEditorFocused = Boolean(api === null || api === void 0 || (_api$focus2 = api.focus) === null || _api$focus2 === void 0 || (_api$focus2 = _api$focus2.sharedState.currentState()) === null || _api$focus2 === void 0 ? void 0 : _api$focus2.hasFocus);
        var _calculateUserInterac = calculateUserInteractionState({
            placeholderState: placeholderState,
            oldEditorState: _oldEditorState,
            newEditorState: newEditorState
          }),
          userHadTyped = _calculateUserInterac.userHadTyped,
          typedAndDeleted = _calculateUserInterac.typedAndDeleted;
        var newPlaceholderState = createPlaceHolderStateFrom({
          isEditorFocused: isEditorFocused,
          editorState: newEditorState,
          isTypeAheadOpen: api === null || api === void 0 || (_api$typeAhead2 = api.typeAhead) === null || _api$typeAhead2 === void 0 ? void 0 : _api$typeAhead2.actions.isOpen,
          defaultPlaceholderText: (_ref3 = (_meta$placeholderText = meta === null || meta === void 0 ? void 0 : meta.placeholderText) !== null && _meta$placeholderText !== void 0 ? _meta$placeholderText : placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.placeholderText) !== null && _ref3 !== void 0 ? _ref3 : defaultPlaceholderText,
          bracketPlaceholderText: bracketPlaceholderText,
          emptyLinePlaceholder: emptyLinePlaceholder,
          placeholderPrompts: (_ref4 = (_meta$placeholderProm = meta === null || meta === void 0 ? void 0 : meta.placeholderPrompts) !== null && _meta$placeholderProm !== void 0 ? _meta$placeholderProm : placeholderState === null || placeholderState === void 0 ? void 0 : placeholderState.placeholderPrompts) !== null && _ref4 !== void 0 ? _ref4 : placeholderPrompts,
          typedAndDeleted: typedAndDeleted,
          userHadTyped: userHadTyped,
          intl: intl
        });

        // Clear timeouts when hasPlaceholder becomes false
        if (!newPlaceholderState.hasPlaceholder) {
          clearAllTypewriterTimeouts();
        }
        return newPlaceholderState;
      }
    },
    props: {
      decorations: function decorations(editorState) {
        var _api$composition, _api$showDiff;
        var _getPlaceholderState = getPlaceholderState(editorState),
          hasPlaceholder = _getPlaceholderState.hasPlaceholder,
          placeholderText = _getPlaceholderState.placeholderText,
          pos = _getPlaceholderState.pos,
          typedAndDeleted = _getPlaceholderState.typedAndDeleted;

        // Decorations is still called after plugin is destroyed
        // So we need to make sure decorations is not called if plugin has been destroyed to prevent the placeholder animations' setTimeouts called infinitely
        if (isDestroyed) {
          return;
        }
        var compositionPluginState = api === null || api === void 0 || (_api$composition = api.composition) === null || _api$composition === void 0 ? void 0 : _api$composition.sharedState.currentState();
        var isShowingDiff = Boolean(api === null || api === void 0 || (_api$showDiff = api.showDiff) === null || _api$showDiff === void 0 || (_api$showDiff = _api$showDiff.sharedState.currentState()) === null || _api$showDiff === void 0 ? void 0 : _api$showDiff.isDisplayingChanges);
        if (hasPlaceholder && ((placeholderText !== null && placeholderText !== void 0 ? placeholderText : '') || placeholderPrompts) && pos !== undefined && !(compositionPluginState !== null && compositionPluginState !== void 0 && compositionPluginState.isComposing) && !isShowingDiff) {
          var initialDelayWhenUserTypedAndDeleted = typedAndDeleted ? TYPEWRITER_TYPED_AND_DELETED_DELAY : 0;
          return createPlaceholderDecoration(editorState, placeholderText !== null && placeholderText !== void 0 ? placeholderText : '', placeholderPrompts, activeTypewriterTimeouts, pos, initialDelayWhenUserTypedAndDeleted);
        }
        return;
      }
    },
    view: function view() {
      return {
        destroy: function destroy() {
          clearAllTypewriterTimeouts();
          isDestroyed = true;
        }
      };
    }
  });
}
export var placeholderPlugin = function placeholderPlugin(_ref5) {
  var options = _ref5.config,
    api = _ref5.api;
  var currentPlaceholder = options === null || options === void 0 ? void 0 : options.placeholder;
  return {
    name: 'placeholder',
    commands: {
      setPlaceholder: function setPlaceholder(placeholderText) {
        return function (_ref6) {
          var tr = _ref6.tr;
          if (currentPlaceholder !== placeholderText) {
            currentPlaceholder = placeholderText;
            return tr.setMeta(pluginKey, {
              placeholderText: placeholderText
            });
          }
          return null;
        };
      },
      setAnimatingPlaceholderPrompts: function setAnimatingPlaceholderPrompts(placeholderPrompts) {
        return function (_ref7) {
          var tr = _ref7.tr;
          return tr.setMeta(pluginKey, {
            placeholderPrompts: placeholderPrompts
          });
        };
      }
    },
    pmPlugins: function pmPlugins() {
      return [{
        name: 'placeholder',
        plugin: function plugin(_ref8) {
          var getIntl = _ref8.getIntl;
          return createPlugin(getIntl(), options && options.placeholder, options && options.placeholderBracketHint, options && options.emptyLinePlaceholder, options && options.placeholderPrompts, api);
        }
      }];
    }
  };
};